import WindowViewModel from '@/ViewModels/Controls/WindowViewModel';
import ComponentManager from '@/Management/ComponentManager';
import {IWindowViewModelConstructorParameter} from '#/DefaultViewExtensionInterfaces';
import TextBoxViewModelT from '@/ViewModels/Controls/TextBoxViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ButtonViewModelT from '@/ViewModels/Controls/ButtonViewModel';
import ButtonViewModelBuilder from '@/Builder/ButtonViewModelBuilder';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import Config from '@/Management/GlobalConfiguration';
import ResourceManager from '@/Management/ResourceManager';
import Utilities from '@/Utility/Utilities';

interface ITwoFactorTokenDialogWindowViewModel extends IWindowViewModelConstructorParameter {
    infoTextErgName: string;
    buttonErgname: string;
    username?: string;
    onSubmit?: (token: string) => JQueryPromise<boolean>;
}

export default class TwoFactorTokenDialogWindowViewModel extends WindowViewModel {

    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModelT>('TextBoxViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');

    private onSubmit: (token: string) => JQueryPromise<boolean>;

    public lockIcon: ImageViewModelT;
    public titleLabel: LabelViewModelT;
    public usernameLabel: LabelViewModelT;
    public infoTextIcon: ImageViewModelT;
    public infoTextLabel: LabelViewModelT;
    public tokenTextBox: TextBoxViewModelT;
    public cancelButton: ButtonViewModelT;
    public submitButton: ButtonViewModelT;

    constructor(params: ITwoFactorTokenDialogWindowViewModel) {
        super(params);

        this.onSubmit = this.handlerOrDefault(params.onSubmit, () => Utilities.resolveImmediately(true));

        this.lockIcon = this.createLockIcon();
        this.titleLabel = this.createTitleLabel();
        this.usernameLabel = this.createUsernameLabel(params.username);
        this.infoTextIcon = this.createInfoTextIcon();
        this.infoTextLabel = this.createInfoTextLabel(params.infoTextErgName);
        this.tokenTextBox = this.createTokenTextBox();
        this.cancelButton = this.createCancelButton();
        this.submitButton = this.createSubmitButton(params.buttonErgname);
    }

    private createLockIcon(): ImageViewModelT {
        return new ImageViewModelBuilder()
            .withParentLocator(this.locator)
            .withLocator('2falock-icon')
            .withSource(Config.inlineImages.TWOFA_LOCK)
            .withAlternativeText()
                .withErgName('DO_LOGIN')
                .done()
            .build();
    }

    private createTitleLabel(): LabelViewModelT {
        return new this.LabelViewModel({
            ergName: 'TWO_FA.TITLE'
        });
    }

    private createUsernameLabel(username?: string): LabelViewModelT {
        const prefix = ResourceManager.getInstance().get('USER_NAME');
        return new this.LabelViewModel({
            caption: `${prefix}: ${username}`
        });
    }

    protected createInfoTextIcon(): ImageViewModelT {
        return new ImageViewModelBuilder()
            .withSource(Config.inlineImages.TWOFA_INFO)
            .build();
    }

    private createInfoTextLabel(infoTextErgName: string): LabelViewModelT {
        return new this.LabelViewModel({
            ergName: infoTextErgName
        });
    }

    private createTokenTextBox(): TextBoxViewModelT {
        return new this.TextBoxViewModel({
            name: 'tokenTextBox',
            label: new this.LabelViewModel({
                ergName: 'TWO_FA.CODE'
            }),
            parentLocator: this.locator,
            locator: 'code',
            onSubmit: () => {
                this.submit();
            }
        });
    }

    private createCancelButton(): ButtonViewModelT {
        return new ButtonViewModelBuilder()
            .withParentLocator(this.locator)
            .withLocator('cancel')
            .withImage()
                .withSource('navigate_left.svg')
                .done()
            .withOnClick(() => {
                this.isOpen(false);
            })
            .build();
    }

    private createSubmitButton(buttonErgname: string): ButtonViewModelT {
        return new ButtonViewModelBuilder()
            .withParentLocator(this.locator)
            .withLocator('submit')
            .withLabel()
                .withErgName(buttonErgname)
                .done()
            .withIsEnabled(this.defineComputed(() => {
                const token = this.tokenTextBox.value();
                return !Utilities.isNullOrUndefinedOrBlank(token);
            }))
            .withOnClick(() => {
                this.submit();
            })
            .build();
    }

    private submit() {
        const token = this.tokenTextBox.value();
        this.onSubmit(token).then(success => {
            if (this.isDisposed) {
                return;
            }

            this.isOpen(false);
        });
    }
}

ComponentManager.setViewModel('TwoFactorTokenDialogWindow', TwoFactorTokenDialogWindowViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');